import { Box, CircularProgress, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { CustomButton } from "../../../components/customButton/CustomButton";
import DataGridGenerico from "../../../components/dataGrid/DataGridGenerico";
import { generateColumnsDefaultConfiguration } from "../../../components/dataGrid/columnHelpers";
import { useGetFilters } from "../hooks/useGetFilters";
import { useGetRows } from "../hooks/useGetRows";
import { useReenviarMail } from "../hooks/useReenviarMail";
import { RvMailFilters } from "./RvMailFilters";

const COLUMNS = generateColumnsDefaultConfiguration([
    { field: "nroLiquidacion", headerName: "#", width: 90 },
    { field: "profesional", headerName: "Profesional", flex: 1, minWidth: 200 },
    { field: "codigoSap", headerName: "Cod. SAP Prof.", width: 160 },
    { field: "periodo", headerName: "Periodo", width: 110 },
    { field: "ocSap", headerName: "OC_SAP", width: 130 },
    {
        field: "total",
        headerName: "Total",
        width: 150,
        valueGetter: (v) =>
            v != null
                ? new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(v)
                : "",
    },
]);

export const RvMailContent = ({ allowedWrite, setChildrenAction }) => {
    const scrollContainerRef = useRef(null);
    // const [scrollHeight, setScrollHeight] = useState("auto");
    const [selectedIds, setSelectedIds] = useState([]);
    const [isRefetchRequired, setIsRefetchRequired] = useState(true);

    const { filters, handleChangeFilters, delegaciones, tiposProfesional } =
        useGetFilters(setIsRefetchRequired);

    const { handleSearch: fetchRows, rows, loading, busquedaCompletada } =
        useGetRows(filters, setIsRefetchRequired);

    const handleSearch = () => {
        setSelectedIds([]);
        fetchRows();
    };

    const { reenviarMail, loading: loadingReenvio } = useReenviarMail({
        onSuccess: handleSearch,
    });

    // ── Scroll height dinámico ────────────────────────────────────────────────

    useEffect(() => {
        const calcHeight = () => {
            if (scrollContainerRef.current) {
                const offsetTop = scrollContainerRef.current.getBoundingClientRect().top;
                // const paddingMainContent = 60;
                // setScrollHeight(`calc(100vh - ${offsetTop}px - ${paddingMainContent}px)`);
            }
        };
        calcHeight();
        window.addEventListener("resize", calcHeight);
        return () => window.removeEventListener("resize", calcHeight);
    }, []);

    // ── Botón header ──────────────────────────────────────────────────────────

    useEffect(() => {
        if (!allowedWrite) return;
        setChildrenAction?.(() => (
            <CustomButton
                variant="contained"
                style={{ width: "130px" }}
                disabled={selectedIds.length === 0 || loadingReenvio}
                onClick={() => reenviarMail(rows, selectedIds)}
            >
                Reenviar Mail
            </CustomButton>
        ));
    }, [setChildrenAction, selectedIds, loadingReenvio, rows, allowedWrite]);

    const sinResultados = useMemo(
        () => busquedaCompletada && !loading && rows.length === 0,
        [busquedaCompletada, loading, rows.length]
    );

    return (
        <div className="grid-container">
            <RvMailFilters
                filters={filters}
                handleChangeFilters={handleChangeFilters}
                handleSearch={handleSearch}
                tiposProfesionalOptions={tiposProfesional}
                delegacionesOptions={delegaciones}
            />

            {isRefetchRequired && !loading && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Typography variant="body1" color="text.secondary">
                        Haz click en "Buscar" para aplicar los nuevos filtros
                    </Typography>
                </Box>
            )}

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <CircularProgress size={40} />
                </Box>
            )}

            {!isRefetchRequired && sinResultados && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Typography variant="body1" color="text.secondary">
                        No se encontraron liquidaciones para los filtros seleccionados.
                    </Typography>
                </Box>
            )}

            {!loading && !isRefetchRequired && rows.length > 0 && (
                <DataGridGenerico
                    columns={COLUMNS}
                    rows={rows}
                    totalCount={rows.length}
                    loading={loading}
                    toolbar={false}
                    checkboxSelection={allowedWrite}
                    rowSelectionModel={selectedIds}
                    onRowSelectionModelChange={(newSelection) => setSelectedIds(newSelection)}
                />
            )}
        </div>
    );
};